const MARLEY_ICON = '/images/favicon/favicon.ico';
const NOTIFICATION_TIMEOUT = 5000;

const isNotificationSupported = (): boolean => 'Notification' in window && window.Notification !== undefined;

export const PERMISSION_STATUS = {
  UNSUPPORTED: 'unsupported',
  DEFAULT: 'default',
  GRANTED: 'granted',
  DENIED: 'denied',
} as const;

type PermissionStatus = typeof PERMISSION_STATUS[keyof typeof PERMISSION_STATUS];

interface NotificationData {
  title: string;
  body: string;
  tag?: string;
}

export const getNotificationPermission = (): PermissionStatus => {
  if (!isNotificationSupported()) {
    return PERMISSION_STATUS.UNSUPPORTED;
  }
  return Notification.permission as PermissionStatus;
};

export const requestNotificationPermission = async (): Promise<PermissionStatus> => {
  if (!isNotificationSupported()) {
    console.debug('This browser does not support notifications');
    return PERMISSION_STATUS.UNSUPPORTED;
  }
  try {
    return await Notification.requestPermission() as PermissionStatus;
  } catch (error) {
    console.error('Error requesting notification permission:', error);
    return PERMISSION_STATUS.UNSUPPORTED;
  }
};

const notificationHandler = ({
  title,
  body,
  tag,
}: NotificationData): void => {
  try {
    const notification = new Notification(title, {
      body,
      icon: MARLEY_ICON,
      tag: tag || title,
      requireInteraction: false,
      silent: false,
    });

    notification.onshow = () => {
      console.debug(body, 'Desktop Notification sent');
    };

    notification.onclick = () => {
      console.debug('Notification clicked');
      window.focus();
      notification.close();
    };

    notification.onclose = () => {
      console.debug('Notification closed');
    };

    notification.onerror = (error: Event) => {
      console.error('Notification error:', error);
    };

    setTimeout(() => {
      notification.close();
    }, NOTIFICATION_TIMEOUT);
  } catch (error) {
    console.error('Error showing notification:', error);
  }
};

export const showNotification = ({
  title,
  body,
  tag,
}: NotificationData): void => {
  if (getNotificationPermission() !== PERMISSION_STATUS.GRANTED) {
    console.debug('Notification permission not granted');
    return;
  }
  notificationHandler({ title, body, tag });
};

export const initializeNotifications = async (): Promise<void> => {
  const permission = getNotificationPermission();

  if (permission === PERMISSION_STATUS.DEFAULT) {
    console.debug('Requesting notification permission...');
    await requestNotificationPermission();
  } else if (permission === PERMISSION_STATUS.GRANTED) {
    console.debug('Notification permission already granted');
  } else if (permission === PERMISSION_STATUS.DENIED) {
    console.debug('Notification permission denied by user');
  }
};
